const post = {
  slug: 'youtube-retention-open-loops',
  cluster: 'scripting',
  eyebrow: 'Scripting',
  title: 'Open loops: the mechanic that keeps people watching',
  description:
    'Tension is not drama. It is an unanswered question held open deliberately — and there is a technique for doing it without feeling like a tease.',
  keywords: [
    'youtube retention', 'open loop', 'video tension', 'audience retention',
    'keeping viewers watching', 'retention techniques', 'script tension',
  ],
  published: '2026-08-21',
  hero: {
    src: '/blog/youtube-retention-open-loops.webp',
    alt: 'A timeline with overlapping arcs, each opening before the previous one closes.',
  },
  lede:
    'People do not keep watching because a video is good. They keep watching because something is unresolved. Those are different properties, and only one of them can be engineered.',
  sections: [
    {
      heading: 'What an open loop is',
      body: [
        {
          type: 'para',
          text: 'A question the viewer wants answered, raised deliberately, and not yet answered.',
        },
        {
          type: 'para',
          text: 'It is not a cliffhanger and it is not withholding for its own sake. The distinction that matters: a good open loop makes visible progress while staying open. The viewer can tell they are getting closer, which is what separates suspense from being strung along.',
        },
      ],
    },
    {
      heading: 'The four rules',
      body: [
        {
          type: 'list',
          ordered: true,
          items: [
            '**Open it early** — in the first two sentences, before anyone has decided to leave.',
            '**Keep it visible** — refer back as you go, so the viewer knows you have not forgotten and this is going somewhere.',
            '**Close it where you planned** — not earlier. Resolving the hook at thirty seconds and narrating an epilogue is the single most common structural failure.',
            '**Open the next one as you close this one** — so there is never a stretch with nothing pending.',
          ],
        },
        {
          type: 'para',
          text: 'Rule four is what turns a single hook into sustained retention. One loop carries perhaps ninety seconds. A chain of them carries a whole video.',
        },
      ],
    },
    {
      heading: 'Order is the tool',
      body: [
        {
          type: 'para',
          text: 'The same sentences in a different sequence are the difference between suspense and a summary. Three moves that cost nothing:',
        },
        {
          type: 'list',
          items: [
            '**State that something went wrong before saying what.** "The launch failed for a reason nobody had modelled" holds attention that "the O-ring seal hardened in the cold" does not, said first.',
            '**Name a consequence before its cause.** The consequence is the interesting half.',
            '**Give a number, explain it a beat later.** The gap is small and it works.',
          ],
        },
        {
          type: 'para',
          text: 'None of this is deception. Every fact is still delivered — you are choosing the order in which the viewer receives them, which is where most of the craft in a script actually lives.',
        },
      ],
    },
    {
      heading: 'Loops that annoy people',
      body: [
        {
          type: 'para',
          text: 'The technique has a failure mode and audiences are sensitised to it.',
        },
        {
          type: 'list',
          items: [
            '**"But first…"** — explicitly announcing a delay. Nobody has ever been pleased by this sentence.',
            '**"Stay till the end."** Asking for patience instead of earning it, and it signals the middle is not worth watching.',
            '**Loops with no progress.** Repeatedly hinting without advancing reads as padding, correctly.',
            '**A payoff smaller than the setup.** If the buildup implies a revelation and delivers a footnote, you have trained the viewer not to trust the next one.',
          ],
        },
        {
          type: 'quote',
          text: 'The test: is the viewer waiting because they want to know, or because you told them to wait? Only the first one works.',
        },
      ],
    },
    {
      heading: 'Planning them',
      body: [
        {
          type: 'para',
          text: 'Before drafting, write two columns: the question, and where it gets answered. Do this for the main loop and for each secondary one.',
        },
        {
          type: 'para',
          text: 'Two things fall out immediately. Any loop with no planned closing point will drift, and any long stretch with nothing in either column is the part of the video where retention will drop. You can see the problem before writing a word of it.',
        },
      ],
    },
  ],
  faq: [
    {
      q: 'How many loops should a video have?',
      a: 'One main one spanning the video, and secondary ones roughly every ninety seconds to two minutes. That is a starting rhythm rather than a rule — what matters is that no long stretch has nothing pending.',
    },
    {
      q: 'Does this work for tutorials?',
      a: 'Yes, in a modified form. The overall loop is "will I be able to do this by the end", and each step can pose a small question the next step answers. What does not work in a tutorial is artificially withholding the practical information — there the viewer\'s patience is genuinely limited.',
    },
  ],
  related: ['youtube-video-script-structure', 'youtube-hook-writing', 'youtube-script-writing'],
};

export default post;
